import React from 'react'

import AddressInput from '@/components/integration/nylas/scheduler/components/meetingFormatPicker/AddressInput'
import OfficeOptionLabel from '@/components/integration/nylas/scheduler/components/meetingFormatPicker/OfficeOptionLabel'
import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import { getOfferedInPersonMeetingFormats } from '@/components/integration/nylas/scheduler/meetingFormatSelection'
import { ClientHomeAddress } from '@/components/integration/nylas/scheduler/types'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  RADIX_EMPTY_VALUE,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MEETING_FORMAT } from '@/lib/constants'

interface Props {
  isBookingAsClient?: boolean
}

/**
 * Where the adviser travels to for a meeting at the client's home, plus (when
 * the company has offices that can handle the visit) which office does, so the
 * times shown are those of the advisers based there.
 */
const ClientHomeAddressFields: React.FC<Props> = ({
  isBookingAsClient = true,
}) => {
  const {
    availableMeetingFormats = [],
    availableHomeVisitLocations = [],
    suggestedClientAddress,
    hasHiddenClientHomeAddress,
    hiddenClientHomeTownOrCity,
  } = useSchedulerConfig()
  const {
    selectedFormat,
    clientHomeAddress,
    setClientHomeAddress,
    selectedLocationId,
    setSelectedLocationId,
  } = useSchedulerSelection()

  if (selectedFormat !== MEETING_FORMAT.in_person_client_home) {
    return null
  }

  // When the client's home is the only in-person option, "In person" is all
  // the person has chosen, so say whose address this is. With both options,
  // "Where?" (see InPersonPlacePicker) has already asked.
  const isHomeTheOnlyInPersonOption =
    getOfferedInPersonMeetingFormats(availableMeetingFormats).length === 1

  const isAddressEmpty =
    clientHomeAddress.addressLine1 === '' &&
    clientHomeAddress.townOrCity === '' &&
    clientHomeAddress.postcode === ''

  function updateAddressPart(
    addressPart: keyof ClientHomeAddress,
    value: string,
  ) {
    setClientHomeAddress({ ...clientHomeAddress, [addressPart]: value })
  }

  return (
    <div className="mb-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {isHomeTheOnlyInPersonOption && (
          <span>
            {isBookingAsClient ? 'Your home address' : "Your client's address"}
          </span>
        )}

        {hasHiddenClientHomeAddress && (
          <p className="text-muted-foreground mb-0 text-sm">
            {hiddenClientHomeTownOrCity
              ? `The meeting stays at the address on file in ${hiddenClientHomeTownOrCity}.`
              : "The meeting stays at the client's address on file."}
          </p>
        )}

        {suggestedClientAddress && isAddressEmpty && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setClientHomeAddress(suggestedClientAddress)}
          >
            Use the address you entered earlier
          </Button>
        )}
      </div>

      <div className="grid gap-4 @md:grid-cols-2">
        {!hasHiddenClientHomeAddress && (
          <>
            <AddressInput
              id="scheduler-client-address-line-1"
              label="Address line 1"
              autoComplete="address-line1"
              value={clientHomeAddress.addressLine1}
              animationIndex={0}
              onChange={(value) => updateAddressPart('addressLine1', value)}
            />

            <AddressInput
              id="scheduler-client-town-or-city"
              label="Town or city"
              autoComplete="address-level2"
              value={clientHomeAddress.townOrCity}
              animationIndex={1}
              onChange={(value) => updateAddressPart('townOrCity', value)}
            />

            <AddressInput
              id="scheduler-client-postcode"
              label="Postcode"
              autoComplete="postal-code"
              value={clientHomeAddress.postcode}
              animationIndex={2}
              onChange={(value) => updateAddressPart('postcode', value)}
            />
          </>
        )}

        {availableHomeVisitLocations.length > 0 && (
          <div
            className="pop-in flex flex-col gap-2"
            style={{
              // Follows the address fields, or comes first when they're hidden
              animationDelay: `${(hasHiddenClientHomeAddress ? 0 : 3) * 64}ms`,
              animationFillMode: 'both',
            }}
          >
            <Label htmlFor="scheduler-home-visit-office">
              Which office will handle this visit?
            </Label>

            <Select
              value={selectedLocationId?.toString() ?? RADIX_EMPTY_VALUE}
              onValueChange={(locationId) =>
                // The Select maps the "Any office" item to '' (see
                // RADIX_EMPTY_VALUE). No office means every adviser's times.
                setSelectedLocationId(locationId ? Number(locationId) : null)
              }
            >
              <SelectTrigger
                id="scheduler-home-visit-office"
                className="w-full"
              >
                <SelectValue placeholder="Any office" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value={RADIX_EMPTY_VALUE}>Any office</SelectItem>

                {availableHomeVisitLocations.map((location) => (
                  <SelectItem key={location.id} value={String(location.id)}>
                    <OfficeOptionLabel location={location} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <p className="text-muted-foreground mb-0 text-xs">
              {isBookingAsClient
                ? 'Choose the office nearest to you. Times below are for advisers based there.'
                : 'Choose the office nearest the client. Times below are for advisers based there.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientHomeAddressFields
